import React, { useState, useEffect } from 'react';

function RateCards() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRate, setCurrentRate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    destination_prefix: '',
    rate_per_minute: 0,
    billing_increment: 60,
    connection_fee: 0
  });

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setRates(data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      if (editMode) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rates/${currentRate.rate_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          alert('Rate card updated successfully');
          fetchRates();
          setShowModal(false);
        }
      } else {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rates`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          alert('Rate card created successfully');
          fetchRates();
          setShowModal(false);
        }
      }
    } catch (error) {
      console.error('Error saving rate:', error);
      alert('Error saving rate card');
    }
  };

  const handleEdit = (rate) => {
    setCurrentRate(rate);
    setFormData({
      name: rate.name,
      destination_prefix: rate.destination_prefix,
      rate_per_minute: rate.rate_per_minute,
      billing_increment: rate.billing_increment,
      connection_fee: rate.connection_fee
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this rate card?')) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rates/${rateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Rate card deleted successfully');
        fetchRates();
      }
    } catch (error) {
      console.error('Error deleting rate:', error);
      alert('Error deleting rate card');
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setCurrentRate(null);
    setFormData({
      name: '',
      destination_prefix: '',
      rate_per_minute: 0,
      billing_increment: 60,
      connection_fee: 0
    });
    setShowModal(true);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>Rate Cards</h2>
        <button className="btn-primary" onClick={openAddModal}>Add Rate Card</button>
      </div>
      
      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Destination Prefix</th>
              <th>Rate/Min ($)</th>
              <th>Billing Increment (s)</th>
              <th>Connection Fee ($)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rates.map(rate => (
              <tr key={rate.rate_id}>
                <td>{rate.name}</td>
                <td><code className="code-text">{rate.destination_prefix}</code></td>
                <td>${rate.rate_per_minute?.toFixed(4)}</td>
                <td>{rate.billing_increment}s</td>
                <td>${rate.connection_fee?.toFixed(4)}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(rate)}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(rate.rate_id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editMode ? 'Edit Rate Card' : 'Add New Rate Card'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Destination Prefix:</label>
                <input
                  type="text"
                  value={formData.destination_prefix}
                  onChange={(e) => setFormData({...formData, destination_prefix: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Rate per Minute ($):</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.rate_per_minute}
                  onChange={(e) => setFormData({...formData, rate_per_minute: parseFloat(e.target.value)})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Billing Increment (seconds):</label>
                <input
                  type="number"
                  value={formData.billing_increment}
                  onChange={(e) => setFormData({...formData, billing_increment: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Connection Fee ($):</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.connection_fee}
                  onChange={(e) => setFormData({...formData, connection_fee: parseFloat(e.target.value)})}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-primary">{editMode ? 'Update' : 'Create'}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RateCards;
